/**
 * UIInputSystem (W-UI, UI-7) — pointer + focus + keyboard/gamepad navigation over mounted
 * canvases. Player-safe and host-agnostic: the host (editor/player UI subsystem) polls its
 * input devices into plain {@link UIPointerSnapshot}/{@link UINavSnapshot} state snapshots
 * once per frame; THIS system does all edge detection, hit-testing and event dispatch, so
 * editor and player cannot drift on semantics.
 *
 * Semantics (design §Input):
 * - Hit-test: top-most canvas first (sortOrder DESC), reverse painter's order inside a tree,
 *   `overflow:hidden` clips children, `pointer-events` honored via nearest-ancestor lookup.
 *   A node is a hit TARGET only if it is "interactive or visibly painted" (button/image,
 *   any handler, opaque background/border/slice, or explicit `pointer-events: auto`) —
 *   transparent layout containers do NOT swallow the pointer (Unity raycast-target analog;
 *   full-screen HUD roots stay click-through without opt-outs).
 * - Pointer events: enter/leave (non-bubbling, ancestor-chain diff, CSS-like `:hover` on the
 *   whole path), move/down/up/click/wheel (bubbling, `stopPropagation()` honored). Primary
 *   button captures on pointerdown: move/up route to the capture node until release; `click`
 *   fires on the capture node when released over it (or a descendant).
 * - Focus: one focused node per system (`:focus` pseudo-class, non-bubbling focus/blur).
 *   Focusable = `<button>` or a truthy `focusable` attr, not disabled. Pointerdown focuses
 *   the nearest focusable in the hit chain (and blurs when none).
 * - Navigation: direction edges (with hold-repeat) run Unity-style spatial nav across all
 *   visible canvases; explicit `nav-up/down/left/right` attrs naming another node's `id`
 *   attr win. `submit` dispatches `click` on the focused node, `cancel` dispatches `cancel`.
 * - Consumption is ADVISORY (resolved decision #2): `pointerOverUI` is true when the pointer
 *   is over a hit target (or a drag is captured); gameplay opts in via `engine.ui`.
 */
import type { UIDocument } from '../tree/UIDocument';
import type { UINode } from '../tree/UINode';
import type { UICanvasView } from '../render/UIPass';
import { fromTransform, invert } from '../render/mat2d';
import { UIEvent, type UIEventType } from './UIEvents';

/** Pointer state for one frame, in PHYSICAL viewport pixels (backing-canvas space). */
export interface UIPointerSnapshot {
  x: number;
  y: number;
  /** Pointer is over the game viewport at all (host maps + bounds-checks). */
  inside: boolean;
  /** Held state per button — the system edge-detects against the previous frame. */
  primary: boolean;
  secondary: boolean;
  middle: boolean;
  wheelX: number;
  wheelY: number;
}

/** Navigation state for one frame (held booleans — the system edge-detects + repeats). */
export interface UINavSnapshot {
  up: boolean;
  down: boolean;
  left: boolean;
  right: boolean;
  submit: boolean;
  cancel: boolean;
}

export const EMPTY_POINTER: UIPointerSnapshot = {
  x: -1,
  y: -1,
  inside: false,
  primary: false,
  secondary: false,
  middle: false,
  wheelX: 0,
  wheelY: 0,
};

export const EMPTY_NAV: UINavSnapshot = {
  up: false,
  down: false,
  left: false,
  right: false,
  submit: false,
  cancel: false,
};

type NavDir = 'up' | 'down' | 'left' | 'right';
const NAV_DIRS: readonly NavDir[] = ['up', 'down', 'left', 'right'];

/** Hold-to-repeat pacing for directional navigation (seconds). */
const NAV_REPEAT_DELAY = 0.4;
const NAV_REPEAT_INTERVAL = 0.14;

interface NodeRef {
  node: UINode;
  view: UICanvasView;
}

interface FocusCandidate extends NodeRef {
  /** Center in PHYSICAL px — comparable across canvases with different scalers. */
  cx: number;
  cy: number;
}

export interface UIInputSystemOptions {
  /** Sink for exceptions thrown by UI event handlers (host backs with the engine logger). */
  onHandlerError?: (eventType: UIEventType, error: unknown) => void;
}

export class UIInputSystem {
  constructor(
    private readonly getViews: () => readonly UICanvasView[],
    private readonly options: UIInputSystemOptions = {},
  ) {}

  private hoverPath: UINode[] = []; // root-first; last = hover target
  private hoverView: UICanvasView | null = null;
  private captured: NodeRef | null = null;
  private focused: NodeRef | null = null;

  private prev: UIPointerSnapshot = { ...EMPTY_POINTER };
  private prevNav: UINavSnapshot = { ...EMPTY_NAV };
  private navDir: NavDir | null = null;
  private navTimer = 0;

  private consumed = false;

  /** Advisory pointer consumption for gameplay (`engine.ui.pointerOverUI()`), fresh per update. */
  get pointerOverUI(): boolean {
    return this.consumed;
  }

  get focusedNode(): UINode | null {
    return this.focused?.node ?? null;
  }

  /** Per-frame pump. Host supplies snapshots; dt drives navigation hold-repeat. */
  update(dt: number, pointer: UIPointerSnapshot, nav: UINavSnapshot): void {
    this.pruneDisconnected();

    const views = [...this.getViews()]
      .filter((v) => v.visible)
      .sort((a, b) => b.sortOrder - a.sortOrder); // top-most first

    const hit = pointer.inside ? this.hitTest(views, pointer.x, pointer.y) : null;

    this.syncHover(hit, pointer);
    this.firePointerMove(pointer, hit);
    this.fireButtons(pointer, hit);
    this.fireWheel(pointer, hit);
    this.updateNavigation(dt, nav, views);

    this.consumed = this.captured !== null || hit !== null;
    this.prev = { ...pointer };
    this.prevNav = { ...nav };
  }

  /** Drop all interaction state (play stopped / canvases torn down). Silent — no events. */
  reset(): void {
    for (const node of this.hoverPath) this.hoverView?.document.setState(node, 'hover', false);
    if (this.captured) this.setActivePath(this.captured, false);
    if (this.focused) this.focused.view.document.setState(this.focused.node, 'focus', false);
    this.hoverPath = [];
    this.hoverView = null;
    this.captured = null;
    this.focused = null;
    this.prev = { ...EMPTY_POINTER };
    this.prevNav = { ...EMPTY_NAV };
    this.navDir = null;
    this.navTimer = 0;
    this.consumed = false;
  }

  // -------------------------------------------------------------------------
  // hit-testing

  private hitTest(views: readonly UICanvasView[], px: number, py: number): NodeRef | null {
    for (const view of views) {
      const scale = view.scale > 0 ? view.scale : 1;
      const lx = px / scale;
      const ly = py / scale;
      const root = view.document.root;
      for (let i = root.children.length - 1; i >= 0; i--) {
        const node = this.hitNode(view.document, root.children[i]!, lx, ly);
        if (node) return { node, view };
      }
    }
    return null;
  }

  private hitNode(doc: UIDocument, node: UINode, x: number, y: number): UINode | null {
    if (node.kind !== 'element') return null;
    const style = node.style;
    if (style.display === 'none') return null;

    // UI-17: a `transform` maps the node's box in PAINT — inverse-map the pointer into the node's
    // untransformed (layout) frame so the axis-aligned rect test still holds; children paint in this
    // transformed space, so they hit-test in the mapped coords too. Logical units (scale 1).
    let hx = x;
    let hy = y;
    if (style.transform && style.transform.length > 0) {
      const m = fromTransform(
        style.transform,
        style.transformOrigin ?? { x: 0.5, y: 0.5 },
        node.layoutX,
        node.layoutY,
        node.layoutW,
        node.layoutH,
        1,
      );
      const inv = invert(m);
      if (!inv) return null; // degenerate (scale 0 / edge-on tilt) — nothing under it is hittable
      // homogeneous divide (UI-17c) — g/h are 0 for affine, so this is exact for the 2D case too
      const w = inv.g * x + inv.h * y + 1;
      if (Math.abs(w) < 1e-9) return null;
      hx = (inv.a * x + inv.c * y + inv.e) / w;
      hy = (inv.b * x + inv.d * y + inv.f) / w;
    }

    const inRect =
      hx >= node.layoutX &&
      hx < node.layoutX + node.layoutW &&
      hy >= node.layoutY &&
      hy < node.layoutY + node.layoutH;
    if ((style.overflow === 'hidden' || style.overflow === 'scroll') && !inRect) return null; // clips children too

    for (let i = node.children.length - 1; i >= 0; i--) {
      const hit = this.hitNode(doc, node.children[i]!, hx, hy);
      if (hit) return hit;
    }
    return inRect && this.isHitTarget(doc, node) ? node : null;
  }

  /** "Interactive or visibly painted" — see header. `pointer-events` wins in both directions. */
  private isHitTarget(doc: UIDocument, node: UINode): boolean {
    if (doc.inherited(node, 'pointerEvents') === 'none') return false;
    if (node.style.pointerEvents === 'auto') return true; // explicit own opt-in
    if (node.elementType === 'button' || node.elementType === 'image') return true;
    if (node.handlers.size > 0) return true;
    const s = node.style;
    if (s.backgroundColor && s.backgroundColor[3] > 0) return true;
    if (s.backgroundImage) return true;
    if (s.material) return true; // material-shaded fill (UI-19)
    if ((s.backdropBlur ?? 0) > 0) return true; // acrylic panel paints its footprint (UI-16)
    if ((s.borderWidth ?? 0) > 0 && s.borderColor && s.borderColor[3] > 0) return true;
    return false;
  }

  // -------------------------------------------------------------------------
  // hover (enter/leave + :hover path)

  private syncHover(hit: NodeRef | null, pointer: UIPointerSnapshot): void {
    const newPath = hit ? chainOf(hit.node) : [];
    const oldPath = this.hoverPath;
    if (pathsEqual(oldPath, newPath) && this.hoverView === (hit?.view ?? null)) return;

    const newSet = new Set(newPath);
    const oldView = this.hoverView;
    for (let i = oldPath.length - 1; i >= 0; i--) {
      const node = oldPath[i]!;
      if (newSet.has(node) && oldView === hit?.view) continue;
      oldView?.document.setState(node, 'hover', false);
      if (node.isConnected()) {
        const { lx, ly } = this.toLogical(pointer, oldView);
        this.dispatch(node, new UIEvent('pointerleave', node, { x: lx, y: ly }), false);
      }
    }
    const oldSet = new Set(oldView === hit?.view ? oldPath : []);
    for (const node of newPath) {
      if (oldSet.has(node)) continue;
      hit!.view.document.setState(node, 'hover', true);
      const { lx, ly } = this.toLogical(pointer, hit!.view);
      this.dispatch(node, new UIEvent('pointerenter', node, { x: lx, y: ly }), false);
    }

    this.hoverPath = newPath;
    this.hoverView = hit?.view ?? null;
  }

  // -------------------------------------------------------------------------
  // pointer events

  private firePointerMove(pointer: UIPointerSnapshot, hit: NodeRef | null): void {
    if (pointer.x === this.prev.x && pointer.y === this.prev.y) return;
    const to = this.captured ?? hit;
    if (!to) return;
    const { lx, ly } = this.toLogical(pointer, to.view);
    this.dispatch(to.node, new UIEvent('pointermove', to.node, { x: lx, y: ly }), true);
  }

  private fireButtons(pointer: UIPointerSnapshot, hit: NodeRef | null): void {
    const buttons: Array<{ held: boolean; was: boolean; id: number }> = [
      { held: pointer.primary, was: this.prev.primary, id: 0 },
      { held: pointer.middle, was: this.prev.middle, id: 1 },
      { held: pointer.secondary, was: this.prev.secondary, id: 2 },
    ];

    for (const b of buttons) {
      if (b.held && !b.was) this.onButtonDown(b.id, pointer, hit);
      else if (!b.held && b.was) this.onButtonUp(b.id, pointer, hit);
    }
  }

  private onButtonDown(button: number, pointer: UIPointerSnapshot, hit: NodeRef | null): void {
    // while captured, every button routes to the capture node (DOM pointer-capture semantics)
    const to = this.captured ?? hit;
    if (button === 0 && !this.captured && hit) {
      if (chainDisabled(hit.node)) return; // disabled swallows the press, no events/focus
      this.captured = hit;
      this.setActivePath(hit, true);
      this.focus(nearestFocusable(hit.node), hit.view);
    } else if (button === 0 && !hit) {
      this.focus(null, null); // primary press on empty space blurs
    }
    if (!to || chainDisabled(to.node)) return;
    const { lx, ly } = this.toLogical(pointer, to.view);
    this.dispatch(to.node, new UIEvent('pointerdown', to.node, { x: lx, y: ly, button }), true);
  }

  private onButtonUp(button: number, pointer: UIPointerSnapshot, hit: NodeRef | null): void {
    const to = this.captured ?? hit;
    if (to && !chainDisabled(to.node)) {
      const { lx, ly } = this.toLogical(pointer, to.view);
      this.dispatch(to.node, new UIEvent('pointerup', to.node, { x: lx, y: ly, button }), true);
    }
    if (button !== 0) return;

    const captured = this.captured;
    if (captured) {
      this.setActivePath(captured, false);
      this.captured = null;
      // click = released over the capture node or one of its descendants
      if (hit && captured.node.isConnected() && chainOf(hit.node).includes(captured.node)) {
        const { lx, ly } = this.toLogical(pointer, captured.view);
        this.dispatch(captured.node, new UIEvent('click', captured.node, { x: lx, y: ly, button }), true);
      }
    }
  }

  private fireWheel(pointer: UIPointerSnapshot, hit: NodeRef | null): void {
    if (pointer.wheelX === 0 && pointer.wheelY === 0) return;
    const to = this.captured ?? hit;
    if (!to || chainDisabled(to.node)) return;
    const { lx, ly } = this.toLogical(pointer, to.view);
    this.dispatch(
      to.node,
      new UIEvent('wheel', to.node, { x: lx, y: ly, deltaX: pointer.wheelX, deltaY: pointer.wheelY }),
      true,
    );
    // default action: scroll the nearest `overflow: scroll` ancestor (deltas → logical px);
    // a container at its edge passes the wheel further up (nested scroll chaining)
    const scale = to.view.scale > 0 ? to.view.scale : 1;
    let n: UINode | null = to.node;
    while (n) {
      if (
        n.style.overflow === 'scroll' &&
        to.view.document.scrollBy(n, pointer.wheelX / scale, pointer.wheelY / scale)
      ) {
        break;
      }
      n = n.parent;
    }
  }

  // -------------------------------------------------------------------------
  // focus + navigation

  /** Move focus (null blurs). Fires non-bubbling blur/focus + flips the `:focus` pseudo-state. */
  focus(node: UINode | null, view: UICanvasView | null): void {
    if ((this.focused?.node ?? null) === node) return;
    const old = this.focused;
    this.focused = node && view ? { node, view } : null;
    if (old) {
      old.view.document.setState(old.node, 'focus', false);
      if (old.node.isConnected()) this.dispatch(old.node, new UIEvent('blur', old.node), false);
    }
    if (this.focused) {
      this.focused.view.document.setState(this.focused.node, 'focus', true);
      this.dispatch(this.focused.node, new UIEvent('focus', this.focused.node), false);
    }
  }

  private updateNavigation(dt: number, nav: UINavSnapshot, views: readonly UICanvasView[]): void {
    // direction with hold-repeat: keep repeating the current dir while held, else take a new one
    const held = NAV_DIRS.filter((d) => nav[d]);
    if (this.navDir && !nav[this.navDir]) this.navDir = null;
    const fresh = held.find((d) => !this.prevNav[d]);
    if (fresh) {
      this.navDir = fresh;
      this.navTimer = NAV_REPEAT_DELAY;
      this.navigate(fresh, views);
    } else if (this.navDir) {
      this.navTimer -= dt;
      if (this.navTimer <= 0) {
        this.navTimer += NAV_REPEAT_INTERVAL;
        this.navigate(this.navDir, views);
      }
    }

    if (nav.submit && !this.prevNav.submit && this.focused && !chainDisabled(this.focused.node)) {
      this.dispatch(this.focused.node, new UIEvent('click', this.focused.node, { button: 0 }), true);
    }
    if (nav.cancel && !this.prevNav.cancel && this.focused) {
      this.dispatch(this.focused.node, new UIEvent('cancel', this.focused.node), true);
    }
  }

  private navigate(dir: NavDir, views: readonly UICanvasView[]): void {
    const candidates = collectFocusables(views);
    if (!candidates.length) return;

    const current = this.focused;
    if (!current) {
      // nothing focused: enter the graph at the top-left-most focusable
      const first = candidates.reduce((a, b) => (b.cy < a.cy || (b.cy === a.cy && b.cx < a.cx) ? b : a));
      this.focus(first.node, first.view);
      return;
    }

    // explicit override: nav-<dir> attr names the target's `id` attr
    const override = current.node.attrs[`nav-${dir}`];
    if (typeof override === 'string' && override) {
      const target = candidates.find((c) => c.node.attrs.id === override);
      if (target) {
        this.focus(target.node, target.view);
        return;
      }
    }

    const scale = current.view.scale > 0 ? current.view.scale : 1;
    const fx = (current.node.layoutX + current.node.layoutW / 2) * scale;
    const fy = (current.node.layoutY + current.node.layoutH / 2) * scale;

    let best: FocusCandidate | null = null;
    let bestScore = Infinity;
    for (const c of candidates) {
      if (c.node === current.node) continue;
      const dx = c.cx - fx;
      const dy = c.cy - fy;
      const main = dir === 'left' ? -dx : dir === 'right' ? dx : dir === 'up' ? -dy : dy;
      if (main <= 0) continue; // must be strictly in the pressed direction
      const cross = Math.abs(dir === 'left' || dir === 'right' ? dy : dx);
      const score = main + cross * 2; // Unity-style: distance, orthogonal offset penalized
      if (score < bestScore) {
        bestScore = score;
        best = c;
      }
    }
    if (best) this.focus(best.node, best.view);
  }

  // -------------------------------------------------------------------------
  // plumbing

  /** Invoke handlers target→root (bubbling) or on the target only. Handler errors are contained. */
  private dispatch(target: UINode, event: UIEvent, bubble: boolean): void {
    let n: UINode | null = target;
    while (n) {
      if (n.kind === 'element' && !n.states.has('disabled')) {
        const handler = n.handlers.get(event.type);
        if (handler) {
          event.currentTarget = n;
          for (const fn of Array.isArray(handler) ? handler : [handler]) {
            try {
              (fn as (e: UIEvent) => void)(event);
            } catch (e) {
              if (this.options.onHandlerError) this.options.onHandlerError(event.type, e);
              else console.error(`[ui] ${event.type} handler failed:`, e);
            }
          }
          if (event.propagationStopped) return;
        }
      }
      if (!bubble) return;
      n = n.parent;
    }
  }

  private setActivePath(ref: NodeRef, on: boolean): void {
    for (const node of chainOf(ref.node)) ref.view.document.setState(node, 'active', on);
  }

  private toLogical(pointer: UIPointerSnapshot, view: UICanvasView | null): { lx: number; ly: number } {
    const scale = view && view.scale > 0 ? view.scale : 1;
    return { lx: pointer.x / scale, ly: pointer.y / scale };
  }

  /** Nodes from disposed/remounted canvases must not pin stale hover/capture/focus. */
  private pruneDisconnected(): void {
    if (this.hoverPath.some((n) => !n.isConnected())) {
      this.hoverPath = [];
      this.hoverView = null;
    }
    if (this.captured && !this.captured.node.isConnected()) this.captured = null;
    if (this.focused && !this.focused.node.isConnected()) this.focused = null;
  }
}

// ---------------------------------------------------------------------------
// helpers

/** Element ancestors root-first, target last (root node itself excluded). */
function chainOf(node: UINode): UINode[] {
  const path: UINode[] = [];
  let n: UINode | null = node;
  while (n && n.kind === 'element') {
    path.unshift(n);
    n = n.parent;
  }
  return path;
}

function pathsEqual(a: readonly UINode[], b: readonly UINode[]): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
  return true;
}

/** Disabled anywhere up the chain disables the subtree (panel-level disable). */
function chainDisabled(node: UINode): boolean {
  let n: UINode | null = node;
  while (n) {
    if (n.states.has('disabled')) return true;
    n = n.parent;
  }
  return false;
}

function isFocusable(node: UINode): boolean {
  if (node.kind !== 'element') return false;
  if (node.elementType === 'button') return true;
  const f = node.attrs.focusable;
  return f !== undefined && f !== false && f !== 'false';
}

/** Nearest focusable in the chain (inclusive) — pointerdown focus target. */
function nearestFocusable(node: UINode): UINode | null {
  let n: UINode | null = node;
  while (n) {
    if (isFocusable(n) && !n.states.has('disabled')) return n;
    n = n.parent;
  }
  return null;
}

function collectFocusables(views: readonly UICanvasView[]): FocusCandidate[] {
  const out: FocusCandidate[] = [];
  for (const view of views) {
    const scale = view.scale > 0 ? view.scale : 1;
    const walk = (node: UINode): void => {
      if (node.kind === 'element' && node.style.display === 'none') return;
      if (node.kind === 'element' && isFocusable(node) && !chainDisabled(node)) {
        out.push({
          node,
          view,
          cx: (node.layoutX + node.layoutW / 2) * scale,
          cy: (node.layoutY + node.layoutH / 2) * scale,
        });
      }
      for (const c of node.children) walk(c);
    };
    walk(view.document.root);
  }
  return out;
}
